import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUserRequest,
  ListUsersQuery,
  UpdateUserRequest,
  UserDetails,
} from '@kreart/shared';
import { usersApi } from './api';

const ROOT = ['users'] as const;
export const usersQueryKeys = {
  all: ROOT,
  list: (q: ListUsersQuery) => [...ROOT, 'list', q] as const,
  detail: (id: string) => [...ROOT, 'detail', id] as const,
};

export function useUsers(query: ListUsersQuery, enabled = true) {
  return useQuery({
    queryKey: usersQueryKeys.list(query),
    queryFn: () => usersApi.list(query),
    placeholderData: keepPreviousData,
    enabled,
    meta: { silent: true },
  });
}

export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: id ? usersQueryKeys.detail(id) : ['users', 'detail', 'nil'],
    queryFn: () => usersApi.get(id!),
    enabled: Boolean(id),
    meta: { silent: true },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserRequest) => usersApi.create(body),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateUserRequest) => usersApi.update(id, body),
    meta: { silent: true },
    onSuccess: (data: UserDetails) => {
      qc.setQueryData(usersQueryKeys.detail(id), data);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useResetPassword(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => usersApi.resetPassword(id),
    meta: { silent: true },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: usersQueryKeys.detail(id) });
    },
  });
}

export function useDeleteUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => usersApi.remove(id),
    meta: { silent: true },
    onSuccess: () => {
      qc.removeQueries({ queryKey: usersQueryKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}
